import * as d3 from "d3";
import HOOK from "../../../components/hooks"
import { timing, connectionInfoLayout } from "./nexusConfig"
import { formatDate, getNameProp } from "../../../modules/utilities";
import { CATEGORY, POSITION, POSITION_ENUM, TENSE_ENUM } from "../../../modules/constants"

let showingconnectionInfo = false
let showPositionLocal = false

export const showConnectionInfo = props => {
  // console.log('props:',props)
  const { d, nexus, showPosition } = props
  if (showingconnectionInfo)
    return

  showPositionLocal = showPosition

  showingconnectionInfo = true;

  const connectionInfo = getConnectionInfoDimensions(nexus)
  const capacities = d.connection.capacities
  const hubCategory = nexus && nexus.hubNode && nexus.hubNode.entity && nexus.hubNode.entity.category
  const position = getPosition({ d, hubCategory })
  const displayedRoles = getDisplayedRoles({ capacities, nexus })

  // create connection info group
  let connectionInfoGroup = d3.select(".nexus")
    .append("g")
    .attr("class", "connection-info")
    .attr("transform", "translate(" + (nexus.width * 0.5) + "," + (nexus.height * 0.5) + ")")

  connectionInfoGroup.transition()
    .duration(timing.fast)
    .attr("transform", "translate(" + (connectionInfo.x) + "," + (connectionInfo.y) + ")")

  // create link connection background
  connectionInfoGroup.background = connectionInfoGroup.append("rect")
    .attr("class", "connection-info-background")
    .attr("width", 0)
    .attr("height", 0)
    .attr("rx", 0)
    .attr("ry", 0)
    .attr("x", 0)
    .attr("y", 0)
    .attr("fill", "#444444")
    .attr("stroke", connectionInfoLayout.color)
    .style("opacity", connectionInfoLayout.opacity)
    .attr("cursor", "pointer")
    .on("click", function () {
      hideConnectionInfo();
    })
    .transition()
    .duration(timing.fast)
    .attr("width", connectionInfo.width)
    .attr("height", connectionInfo.height)
    .attr("rx", connectionInfo.width * connectionInfoLayout.cornerRadiusFactor)
    .attr("ry", connectionInfo.width * connectionInfoLayout.cornerRadiusFactor)

  // create connection info text
  d3.select(".connection-info")
    .append("foreignObject")
    .attr("class", "connection-info-text")
    .attr("pointer-events", "none")
    .attr("transform", "translate(" + (connectionInfoLayout.xPadding) + "," + (connectionInfoLayout.yPadding) + ")")
    .attr("width", `${connectionInfo.width - (connectionInfoLayout.xPadding * 2)}`)
    .attr("height", `${connectionInfo.height - (connectionInfoLayout.yPadding * 2)}`)
    .attr("font-size", "0")
    .attr("color", "white")
    .html(getD3SelectHtml({ d, displayedRoles, position }))
    .style("opacity", 1)
    .transition()
    .duration(timing.fast)
    .attr("font-size", connectionInfoLayout.fontSize)

  d3.select(".connection-info")
    .append("text")
    .attr("class", "connection-info-X")
    .attr("pointer-events", "none")
    .attr("x", 0)
    .attr("y", 0)
    .attr("font-size", connectionInfoLayout.fontSize * 1.5)
    .style("fill", "white")
    .text("X")
    .transition()
    .duration(timing.fast)
    .attr("x", `${connectionInfoLayout.xPadding}`)
    .attr("y", `${connectionInfo.height - connectionInfoLayout.yPadding}`)

  return;
}

const getD3SelectHtml = ({ d, displayedRoles, position }) => {
  return displayedRoles.length === 1
    ? getConnectionInfoTextSingle({ d, displayedRoles, position })
    : getconnectionInfoTextList({ d, displayedRoles })
}

const getDisplayedRoles = ({ capacities, nexus }) => {
  // Returns all roles matching the selected tense checkboxes
  const roles = []
  for (var key1 in capacities) { // 'officer, 'psc etc
    capacities[key1].forEach(element => {
      if (!!(nexus.checkboxes[HOOK.TENSE] & element.tenseBit) &&
          !!(nexus.checkboxes[HOOK.POSITION] & element.position.typeBit))        
        roles.push(element)
    })
  }
  return roles
}

const getPosition = ({ d, hubCategory }) => {
  let position = hubCategory === CATEGORY.PERSON
    ? d.target && d.target.entity && d.target.entity.position && d.target.entity.position.name
    : d.source && d.source.entity && d.source.entity.position && d.source.entity.position.name

  return (position === POSITION[POSITION_ENUM.OTHER].NAME)
    ? null
    : position
}

const getConnectionInfoTextSingle = ({ d, displayedRoles, position }) => {
  // Returns text for when there is only a single role displayed
  const role = displayedRoles[0]
  const isWasWillBe = getIsWasWillBe(role.tense)

  position = role && role.position && showPositionLocal
    ? role.position.title
    : role && role.roleDescription
      ? role.roleDescription
      : "Officer"

  let text = `<p class="connection-info-p">
    <strong>${getNameProp.toPresent(d.source.entity.name)}</strong> ${isWasWillBe}</p>`

  text += `<p class="connection-info-p">
  <strong>${position}</strong> of</p>`

  text += `<p class="connection-info-p">
    <strong>${getNameProp.toPresent(d.target.entity.name)}</strong></p>`;

  if (role.from) {
    text += `<p class="connection-info-p">from ${formatDate(role.from, true)}`

    if (/* showPositionLocal && */ role.until)
      text += ` to ${formatDate(role.until, true)}`

    text += "</p>";
  }

  return text;
}

const getconnectionInfoTextList = ({ d, displayedRoles }) => {
  let text = ""
  let prefix = ""
  let suffix = ""

  const currentDisplayedRoles = getRolesByTense(displayedRoles, TENSE_ENUM.CURRENT)
    .concat(getRolesByTense(displayedRoles, TENSE_ENUM.NOTICE))
  const pastDisplayedRoles = getRolesByTense(displayedRoles, TENSE_ENUM.PAST)
  const futureDisplayedRoles = getRolesByTense(displayedRoles, TENSE_ENUM.FUTURE)

  // Current (inc notice) roles
  if (currentDisplayedRoles.length > 0) {
    prefix = "currently has"
    text = getPrimaryHeader(d, currentDisplayedRoles, prefix)
    text += appendConnectionInfoText(currentDisplayedRoles)
  }

  // Past roles
  if (pastDisplayedRoles.length > 0) {
    prefix = "had"

    if (text === "")
      text = getPrimaryHeader(d, pastDisplayedRoles, prefix)
    else {
      if (futureDisplayedRoles.length === 0)
        prefix = "and " + prefix
      suffix = "in the past"
      text += getSecondaryHeader(pastDisplayedRoles, prefix, suffix)
    }

    text += appendConnectionInfoText(pastDisplayedRoles)
  }

  // Future roles
  if (futureDisplayedRoles.length > 0) {
    prefix = "will have"
    if (text === "")
      text = getPrimaryHeader(d, futureDisplayedRoles, prefix)
    else {
      prefix = "and " + prefix
      suffix = "in the future"
      text += getSecondaryHeader(pastDisplayedRoles, prefix, suffix)
    }

    text += appendConnectionInfoText(futureDisplayedRoles)
  }

  return text;
}

const getSecondaryHeader = (roles, prefix, suffix) => {
  return `<p>${prefix} ${getThisOrThese(roles)} ${suffix}:</p>`
}

const getIsWasWillBe = tense => tense === TENSE_ENUM.PAST
  ? "was"
  : tense === TENSE_ENUM.FUTURE
    ? "will be"
    : "is" // Current and Notice

const getThisOrThese = roles => roles.length === 1 ? "this role" : "these roles"

const getRolesByTense = (roles, tense) => roles.filter(role => role.tense === tense)

const getPrimaryHeader = (d, roles, hasHadetc) => {
  const source = getNameProp.toPresent(d.source.entity.name)
  const target = getNameProp.toPresent(d.target.entity.name)
  const thisOrThese = getThisOrThese(roles)
  return `<p class="connection-info-p"><strong>${source}</strong> ${hasHadetc} ${thisOrThese} in <strong>${target}</strong>: </p>`;
}

const appendConnectionInfoText = roles => {
  // console.log('roles:', roles)
  let text = ""

  roles.forEach(role => {
    text += `<p class="connection-info-p">`;
    text += `${showPositionLocal && role.position
      ? role.position.title
      : role.roleDescription
        ? role.roleDescription
        : "Officer"}`

    text += role.from || role.until
      ? " ("
      : ""

    text += role.from
      ? `${formatDate(role.from, true)}-`
      : ""

    text += role.until ? `${formatDate(role.until, true)}` : ""
    text += role.from || role.until
      ? ")"
      : ""

    text += "</p>"
  })

  return text
}

export const resizeConnectionInfo = nexus => {
  if (showingconnectionInfo) {
    const connectionInfo = getConnectionInfoDimensions(nexus);

    // resize connection info group
    d3.select(".connection-info")
      .attr("transform", "translate(" + (connectionInfo.x) + "," + (connectionInfo.y) + ")")

    // resize background
    d3.select(".connection-info-background")
      .attr("width", connectionInfo.width)
      .attr("height", connectionInfo.height)
      .attr("rx", connectionInfo.width * connectionInfoLayout.cornerRadiusFactor)
      .attr("ry", connectionInfo.width * connectionInfoLayout.cornerRadiusFactor)

    // resize text area
    d3.select(".connection-info-text")
      .attr("transform", "translate(" + (connectionInfoLayout.xPadding) + "," + (connectionInfoLayout.yPadding) + ")")
      .attr("width", `${connectionInfo.width - (connectionInfoLayout.xPadding * 2)}`)
      .attr("height", `${connectionInfo.height - (connectionInfoLayout.yPadding * 2)}`)

    // reposition X
    d3.select(".connection-info-X")
      .attr("x", `${connectionInfoLayout.xPadding}`)
      .attr("y", `${connectionInfo.height - connectionInfoLayout.xPadding}`)
  }
}

export const hideConnectionInfo = () => {
  showingconnectionInfo = false;

  // remove connection info group
  d3.select(".connection-info")
    .transition()
    .duration(timing.fast)
    .remove();

  // remove background
  d3.select(".connection-info-background")
    .transition()
    .duration(timing.fast)
    .style("opacity", 0)
    .remove()

  // remove text are
  d3.select(".connection-info-text")
    .transition()
    .duration(timing.fast)
    .style("opacity", 0)
    .remove()

  d3.select(".connection-info-X")
    .transition()
    .duration(timing.fast)
    .style("opacity", 0)
    .remove()
}

const getConnectionInfoDimensions = nexus => {
  const connectionInfo = {
    width: Math.min(connectionInfoLayout.maxWidth, nexus.width - (connectionInfoLayout.xMargin * 2)),
    height: Math.min(connectionInfoLayout.maxHeight, nexus.height - (connectionInfoLayout.yMargin * 1.5)),
  }
  connectionInfo.x = (nexus.width - connectionInfo.width) * 0.5;
  connectionInfo.y = Math.max(connectionInfoLayout.yMargin, (nexus.height - connectionInfo.height) * 0.5);

  return connectionInfo;
}

